import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Shell from "@/components/app/Shell";
import EducationModule from "@/components/app/EducationModule";
import { EDUCATION_MODULES } from "@/data/educationModules";
import api from "@/lib/api";
import { GraduationCap, ArrowLeft, Eye, Wind, ArrowRight, Lock, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { previewStatus, useUnlockModal } from "@/components/app/PreviewGate";

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

export default function Learn() {
  const [params, setParams] = useSearchParams();
  const [openKey, setOpenKey] = useState(null);
  const [views, setViews] = useState({});

  useEffect(() => { api.get("/learn/views").then(({ data }) => setViews(data || {})).catch(() => {}); }, []);

  useEffect(() => {
    const q = params.get("open");
    if (q && EDUCATION_MODULES.some((m) => m.key === q)) setOpenKey(q);
  }, [params]);

  useEffect(() => {
    if (openKey) {
      api.post("/learn/view", { module_key: openKey })
        .then(({ data }) => setViews((v) => ({ ...v, [openKey]: data.last_viewed_at })))
        .catch(() => {});
    }
  }, [openKey]);

  const closeOpen = () => { setOpenKey(null); if (params.get("open")) { params.delete("open"); setParams(params, { replace: true }); } };
  const open = EDUCATION_MODULES.find((m) => m.key === openKey);

  const mostRecentKey = Object.entries(views)
    .filter(([k]) => EDUCATION_MODULES.some((m) => m.key === k))
    .sort((a, b) => new Date(b[1]) - new Date(a[1]))[0]?.[0];

  if (open) {
    return (
      <Shell>
        <button onClick={closeOpen} data-testid="learn-back" className="inline-flex items-center gap-2 text-sm text-ink-mid mb-6"><ArrowLeft size={14} /> All modules</button>
        <EducationModule
          number={open.number}
          total={EDUCATION_MODULES.length}
          title={open.title}
          subtitle={open.subtitle}
          body={open.body}
          moduleKey={open.key}
          viewed={true}
          onMarkViewed={() => {}}
          onContinue={closeOpen}
          testidBase={`learn-${open.key}`}
        />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-widest text-mauve font-semibold flex items-center gap-2"><GraduationCap size={12} /> Learn</div>
        <h1 className="font-display text-3xl font-bold text-navy mt-1">Revisit any education module</h1>
        <p className="text-ink-mid mt-2">The three foundational modules — always available to re-watch and re-read.</p>
      </div>

      <Link
        to="/learn/breathe-framework"
        data-testid="learn-card-breathe-framework"
        className="block bg-navy text-warm-white rounded-3xl p-6 md:p-8 mb-4 border border-navy-deep hover:-translate-y-1 transition-all shadow-[0_8px_30px_rgba(44,45,91,0.15)]"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-gold text-navy font-display font-bold text-lg flex items-center justify-center">
            <Wind size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-gold font-semibold">Foundational · always available</div>
            <div className="font-display font-bold text-warm-white text-xl mt-1">The BREATHE Framework in Detail</div>
            <p className="mt-2 text-sm text-warm-white/80 leading-relaxed">
              The seven pieces the whole program is built on — Bring Them On Board, Recognize and Assess,
              Eliminate Barriers, Activate Passive Reprogramming, Train Strength and Function, Harness Daily Practice,
              Elevate Breathing For Life.
            </p>
          </div>
          <ArrowRight className="text-gold flex-shrink-0 mt-2" />
        </div>
      </Link>

      {/* Myo Parent Playbook — bonus resource card (PDF + companion video). Deliberately
          styled as a "bonus" (mauve/cream, not navy) so it visually differs from the core
          BREATHE Framework module above. Linked to /learn/parent-playbook where both the
          PDF button and the "Create a Compelling Story" video render together. */}
      <Link
        to="/learn/parent-playbook"
        data-testid="learn-card-parent-playbook"
        className="block bg-mauve-pale border-2 border-mauve/40 rounded-3xl p-6 md:p-8 mb-10 hover:-translate-y-1 hover:border-mauve transition-all"
      >
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-mauve text-warm-white flex items-center justify-center">
            <BookOpen size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-mauve font-semibold">Bonus resource · PDF + video</div>
            <div className="font-display font-bold text-navy text-xl mt-1">Myo Parent Playbook</div>
            <p className="mt-2 text-sm text-ink-mid leading-relaxed">
              Fun ideas, games, and strategies for getting your child excited and cooperative with their sessions.
            </p>
          </div>
          <ArrowRight className="text-mauve flex-shrink-0 mt-2" />
        </div>
      </Link>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
        {EDUCATION_MODULES.map((m) => {
          const rel = relativeTime(views[m.key]);
          const isRecent = mostRecentKey === m.key;
          const isGoals = m.key === "goals_terms";
          return (
            <LearnCard key={m.key} m={m} rel={rel} isRecent={isRecent} locked={isGoals} onOpen={() => setOpenKey(m.key)} />
          );
        })}
      </div>
    </Shell>
  );
}

function LearnCard({ m, rel, isRecent, locked, onOpen }) {
  const { user } = useAuth();
  const unlock = useUnlockModal();
  const preview = previewStatus(user);
  const isLocked = locked && preview.isPreview;
  const onClick = () => {
    if (isLocked) { unlock.open("Goals & Terms opens when you enroll. It's the shared vocabulary the check-in uses."); return; }
    onOpen();
  };
  return (
    <button
      onClick={onClick}
      data-testid={isLocked ? `learn-card-${m.key}-locked` : `learn-card-${m.key}`}
      className={`text-left bg-warm-white border rounded-3xl p-6 hover:-translate-y-1 transition-all relative ${isLocked ? "border-mauve/40 opacity-80" : "border-edge hover:border-gold"}`}
    >
      {isLocked && (
        <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-mauve/10 border border-mauve/30 flex items-center justify-center">
          <Lock size={12} className="text-mauve" />
        </div>
      )}
      <div className="text-xs uppercase tracking-widest text-ink-soft font-semibold">Module {m.number}</div>
      <div className="font-display font-bold text-navy text-lg mt-1">{m.title}</div>
      <p className="mt-2 text-sm text-ink-mid line-clamp-3">{m.subtitle}</p>
      <div className="mt-4 flex items-center gap-2 text-xs">
        <Eye size={12} className={rel ? "text-mauve" : "text-ink-soft/50"} />
        <span className={rel ? "text-ink-mid" : "text-ink-soft/60"}>{rel ? `Last viewed ${rel}` : "Not viewed yet"}</span>
        {isRecent && rel && (
          <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gold/20 text-navy">Most recent</span>
        )}
      </div>
    </button>
  );
}
